<script lang="ts" setup>
import { BadgeCheck, BadgeX } from '@lucide/vue';
import { watchDeep } from '@vueuse/core';
import { CollapsibleContent, CollapsibleRoot } from 'reka-ui';
import { ref, toValue } from 'vue';
import type { AddressDetails } from './address-auto-complete.types.js';
import { AddressInput, ManualButton } from './components/index.js';
import { useZipcodeDetails, useZipcodeValidation } from './services/zipcode.service.js';

type TranslationObject = {
  zipcode: string;
  house_number: string;
  extension: string;
  street: string;
  city: string;
  state: string;
  country: string;
};

type LabelTranslationObject = TranslationObject & {
  reset: string;
  searchManual: string;
  noResults: string;
  searchError: string;
};

//-- definitions
defineProps<{
  label: string;
  placeholder: string;
  isFormSubmitted: boolean;
  labels: LabelTranslationObject;
  placeholders: TranslationObject;
}>();

defineEmits<{ addressSelected: [addressDetails: AddressDetails] }>();

//-- State
const isManual = ref<boolean>(false);
const manualSelection = ref<Partial<AddressDetails>>({
  country: 'The Netherlands',
  city: '',
  street: '',
  postcode: '',
  house_number: undefined,
  extension: '',
  municipality: '',
  province: '',
});

const { validate, isValid: isZipcodeValid } = useZipcodeValidation();
const { getData, addressDetails } = useZipcodeDetails();

//-- Model
const modelValue = defineModel<AddressDetails | undefined>();

watchDeep(manualSelection, (selection) => {
  if (!selection) {
    modelValue.value = undefined;
  } else {
    modelValue.value = {
      country: selection.country ?? '',
      city: selection.city ?? '',
      street: selection.street ?? '',
      postcode: selection.postcode ?? '',
      house_number: selection.house_number ?? 0,
      extension: selection.extension ?? null,
      municipality: selection.municipality ?? '',
      province: selection.province ?? '',
    } as AddressDetails;
  }
});

//-- Methods
async function checkAddressPrefill() {
  const { postcode, house_number, extension } = manualSelection.value;

  if (!postcode || !house_number) {
    return;
  }

  await getData(postcode, house_number, extension ?? undefined);

  const address = toValue(addressDetails);
  if (address?.street) {
    manualSelection.value.street = address.street;
  }

  if (address?.city) {
    manualSelection.value.city = address.city;
  }

  if (address?.province) {
    manualSelection.value.province = address.province;
  }
}

async function checkZipcodeValidity() {
  const { postcode } = manualSelection.value;
  if (!postcode) {
    return;
  }
  await validate(postcode);
  checkAddressPrefill();
}
</script>

<template>
  <fieldset class="address-form" :class="{ ungapped: !isManual }">
    <div class="order-form">
      <y-input-wrapper>
        <label slot="label" class="address-autocomplete__label" for="address-combobox">
          {{ label }}

          <ManualButton @click="() => (isManual = !isManual)">
            {{ labels.searchManual }}
          </ManualButton>
        </label>

        <AddressInput
          :placeholder="placeholder"
          :is-form-submitted="isFormSubmitted"
          :disabled="isManual"
          :no-results-message="labels.noResults"
          :reset-label="labels.reset"
          :search-error-message="labels.searchError"
          @address-selected="(details) => (modelValue = details)"
        />
      </y-input-wrapper>
    </div>
    <CollapsibleRoot :open="isManual">
      <CollapsibleContent class="address-form">
        <div class="order-form order-form-3-cols">
          <y-input-wrapper>
            <label slot="label" for="zipcode">{{ labels.zipcode }}</label>

            <div class="zipcode-wrapper">
              <input
                class="order-form__input-text"
                id="zipcode"
                v-model="manualSelection.postcode"
                :placeholder="placeholders.zipcode"
                :aria-invalid="isFormSubmitted && isManual && !manualSelection.postcode"
                :readonly="!isManual"
                @blur.prevent="checkZipcodeValidity"
              />

              <BadgeX v-if="isZipcodeValid === false" class="zipcode-validation-icon zipcode-validation-icon--invalid" />
              <BadgeCheck v-if="isZipcodeValid" class="zipcode-validation-icon zipcode-validation-icon--valid" />
            </div>
          </y-input-wrapper>

          <y-input-wrapper>
            <label slot="label" for="housenumber">{{ labels.house_number }}</label>
            <input
              class="order-form__input-text"
              id="housenumber"
              v-model="manualSelection.house_number"
              :placeholder="placeholders.house_number"
              :aria-invalid="isFormSubmitted && isManual && !manualSelection.house_number"
              :readonly="!isManual"
              @blur.prevent="checkAddressPrefill"
            />
          </y-input-wrapper>

          <y-input-wrapper>
            <label slot="label" for="extension">{{ labels.extension }}</label>
            <input
              class="order-form__input-text"
              id="extension"
              v-model="manualSelection.extension"
              :placeholder="placeholders.extension"
              :readonly="!isManual"
              @blur.prevent="checkAddressPrefill"
            />
          </y-input-wrapper>
        </div>

        <div class="order-form order-form-2-cols">
          <y-input-wrapper>
            <label slot="label" for="street">{{ labels.street }}</label>

            <input
              class="order-form__input-text"
              id="street"
              v-model="manualSelection.street"
              :placeholder="placeholders.street"
              :aria-invalid="isFormSubmitted && isManual && !manualSelection.street"
              :readonly="!isManual"
            />
          </y-input-wrapper>

          <y-input-wrapper>
            <label slot="label" for="city">{{ labels.city }}</label>

            <input
              class="order-form__input-text"
              id="city"
              v-model="manualSelection.city"
              :placeholder="placeholders.city"
              :aria-invalid="isFormSubmitted && isManual && !manualSelection.city"
              :readonly="!isManual"
            />
          </y-input-wrapper>
        </div>

        <div class="order-form order-form-2-cols">
          <y-input-wrapper>
            <label slot="label" for="province">{{ labels.state }}</label>

            <input
              class="order-form__input-text"
              id="province"
              v-model="manualSelection.province"
              :placeholder="placeholders.state"
              :aria-invalid="isFormSubmitted && isManual && !manualSelection.province"
              :readonly="!isManual"
            />
          </y-input-wrapper>

          <y-input-wrapper>
            <label slot="label" for="country">{{ labels.country }}</label>

            <input class="order-form__input-text" id="country" v-model="manualSelection.country" readonly />
          </y-input-wrapper>
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>
  </fieldset>
</template>

<style>
.address-autocomplete__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.address-form {
  border: 0;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 0;
}

.address-form.ungapped {
  gap: 0;
}

.zipcode-wrapper {
  position: relative;
  width: 100%;
}

.zipcode-wrapper > input {
  width: 100%;
}

.zipcode-validation-icon {
  position: absolute;
  top: 50%;
  right: 1rem;
  transform: translateY(-50%);
  width: 1em;
  height: 1em;
}

.zipcode-validation-icon--invalid {
  color: #f30;
}

.zipcode-validation-icon--valid {
  color: green;
}
</style>
